import {
  ArrowUpDownIcon,
  TriangleDownIcon,
  TriangleUpIcon,
} from "@chakra-ui/icons";
import {
  Box,
  Button,
  ButtonGroup,
  chakra,
  Flex,
  Skeleton,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
} from "@chakra-ui/react";
import * as React from "react";
import { Link } from "react-router-dom";
import {
  Column,
  useGlobalFilter,
  usePagination,
  useSortBy,
  useTable,
} from "react-table";
import DeleteIcon from "./icons/DeleteIcon";
import EditIcon from "./icons/EditIcon";

export type DataTableProps = {
  data: any[];
  columns: Column[];
  globalFilter?: string;
  handleAction: (data: any, action: DataTableAction) => void;
  loading?: boolean;
};

export type DataTableAction = "EDIT" | "DELETE";

export function DataTable({
  data,
  columns,
  globalFilter,
  handleAction,
  loading,
}: DataTableProps) {
  const tableData = React.useMemo(
    () => (loading ? Array<any>(5).fill({}) : data),
    [loading, data]
  );

  const tableColumns = React.useMemo(
    () =>
      loading
        ? columns.map((column) => ({
            ...column,
            Cell: <Skeleton height="20px" />,
          }))
        : columns,
    [loading, columns]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    setGlobalFilter,
    canNextPage,
    canPreviousPage,
    nextPage,
    previousPage,
    rows,
    state: { pageIndex, pageSize },
  } = useTable(
    { columns: tableColumns, data: tableData },
    useGlobalFilter,
    useSortBy,
    usePagination
  );

  const container = React.useRef(null);

  React.useEffect(() => {
    setGlobalFilter(globalFilter);
  }, [globalFilter, setGlobalFilter]);

  return (
    <>
      <Box overflow="auto" whiteSpace="nowrap" ref={container}>
        <Table
          {...getTableProps()}
          style={{
            borderCollapse: "separate",
            borderSpacing: 0,
          }}
        >
          <Thead>
            {headerGroups.map((headerGroup) => (
              <Tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <Th
                    width={column.width}
                    {...column.getHeaderProps(column.getSortByToggleProps())}
                  >
                    <Flex alignItems="center">
                      {column.render("Header")}
                      <chakra.span pl="4" mb={1}>
                        {column.isSorted ? (
                          column.isSortedDesc ? (
                            <TriangleDownIcon aria-label="sorted descending" />
                          ) : (
                            <TriangleUpIcon aria-label="sorted ascending" />
                          )
                        ) : (
                          <ArrowUpDownIcon aria-label="sort columns" />
                        )}
                      </chakra.span>
                    </Flex>
                  </Th>
                ))}
                <Th></Th>
              </Tr>
            ))}
          </Thead>

          <Tbody {...getTableBodyProps()}>
            {page.map((row: any) => {
              prepareRow(row);
              return (
                <Tr {...row.getRowProps()}>
                  {row.cells.map((cell: any) => (
                    <Td
                      {...cell.getCellProps()}
                      isNumeric={cell.column.isNumeric}
                    >
                      {cell.column.Header === "Owned By" ? (
                        <Button
                          as={Link}
                          to={`/quotes/users/${row.original.userId}`}
                          variant="link"
                        >
                          {cell.render("Cell")}
                        </Button>
                      ) : (
                        cell.column.Header !== "Name" && cell.render("Cell")
                      )}

                      {cell.column.Header === "Name" && (
                        <Button
                          as={Link}
                          to={{
                            pathname: `/quotes/${row.original.id}/edit`,
                            search: `?type=${row.original.quoteType.toLowerCase()}`,
                            state: { lastLocation: window.location.pathname },
                          }}
                          variant="link"
                        >
                          {cell.render("Cell")}
                        </Button>
                      )}
                    </Td>
                  ))}

                  <Td>
                    <ButtonGroup spacing={4}>
                      <Button
                        as={Link}
                        to={{
                          pathname: `/quotes/${row.original.id}/edit`,
                          search: `?type=${row.original.quoteType.toLowerCase()}`,
                          state: { lastLocation: window.location.pathname },
                        }}
                        leftIcon={<EditIcon />}
                        variant="link"
                      >
                        Edit
                      </Button>
                      <Button
                        leftIcon={<DeleteIcon />}
                        onClick={() => handleAction(row.original, "DELETE")}
                        variant="link"
                      >
                        Delete
                      </Button>
                    </ButtonGroup>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      </Box>
      <Box paddingY={4} marginX={6}>
        <Flex justifyContent="space-between" alignItems="center">
          <Box display={{ base: "none", md: "block" }}>
            {rows.length > 0 ? (
              <Text fontSize="sm" as="p">
                Showing
                <Text fontWeight="medium" as="span">
                  &nbsp;{pageIndex * pageSize + 1}&nbsp;
                </Text>
                to
                <Text fontWeight="medium" as="span">
                  &nbsp;
                  {pageIndex * pageSize + 1 + pageSize > rows.length
                    ? rows.length
                    : pageIndex * pageSize + pageSize}
                  &nbsp;
                </Text>
                of
                <Text fontWeight="medium" as="span">
                  &nbsp;{rows.length ?? tableData.length}&nbsp;
                </Text>
                results
              </Text>
            ) : (
              <Text fontSize="sm" as="p">
                Showing
                <Text fontWeight="medium" as="span">
                  &nbsp;0&nbsp;
                </Text>
                Results
              </Text>
            )}
          </Box>
          <ButtonGroup
            spacing={2}
            flexDir={{ base: "column", sm: "row" }}
            flex={{ base: "auto", md: "none" }}
            justifyContent={{ base: "space-between", md: "normal" }}
          >
            <Button
              disabled={!canPreviousPage}
              onClick={() => previousPage()}
              variant="outline"
              size="sm"
              marginBottom={{ base: 4, md: 0 }}
            >
              Previous Page
            </Button>
            <Button
              disabled={!canNextPage}
              onClick={() => nextPage()}
              variant="outline"
              size="sm"
            >
              Next Page
            </Button>
          </ButtonGroup>
        </Flex>
      </Box>
    </>
  );
}
